import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { labelAPI, recallAPI } from '../../services/millAPI';
import './Mill.css';

// ── payload types ────────────────────────────────────────────────────────────
interface TraceRow {
  channel: string;
  precision: 'EXACT' | 'CANDIDATE_BIN' | 'CANDIDATE_PRODUCT' | 'PARTNER_WINDOW';
  ref: string;
  customer_name: string;
  customer_kind: string;
  customer_email: string;
  customer_phone: string;
  product: string;
  formula: string;
  batch_numbers: string[];
  quantity_lbs: number | null;
  quantity_display: string;
  date: string | null;
  self_check_supported: boolean;
  note: string;
}
interface TraceBatch {
  batch_number: string;
  formula: string;
  completed_at: string | null;
  milled_date: string | null;
}
interface TraceSummary {
  row_count: number;
  by_precision: Record<string, number>;
  by_channel: Record<string, number>;
  distinct_customers: number;
  total_lbs_known: number;
}
interface TraceResult {
  batches: TraceBatch[];
  rows: TraceRow[];
  notes: string[];
  summary: TraceSummary;
}
interface NoticeRecipient {
  email: string;
  name: string;
  kind: string;
  channels: string[];
  batch_numbers: string[];
  products: string[];
  self_check: boolean;
}
interface NoticePreview {
  preview: boolean;
  subject: string;
  recipient_count: number;
  recipients: NoticeRecipient[];
  excluded_count: number;
  excluded: { customer_name: string; channel: string; ref: string; reason: string }[];
  previews: { self_check: boolean; sample_recipient: string; html: string }[];
}

const PRECISION_LABEL: Record<string, string> = {
  EXACT: 'EXACT',
  CANDIDATE_BIN: 'CANDIDATE (bin)',
  CANDIDATE_PRODUCT: 'CANDIDATE (product)',
  PARTNER_WINDOW: 'PARTNER WINDOW',
};
const PRECISION_COLOR: Record<string, string> = {
  EXACT: '#166534',
  CANDIDATE_BIN: '#92400e',
  CANDIDATE_PRODUCT: '#b45309',
  PARTNER_WINDOW: '#6b21a8',
};

const RecallLookup: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Search form
  const [batchNumbers, setBatchNumbers] = useState(searchParams.get('batch') || '');
  const [formulaId, setFormulaId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formulas, setFormulas] = useState<{ id: number; name: string }[]>([]);

  const [result, setResult] = useState<TraceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Notice panel
  const [showNotice, setShowNotice] = useState(false);
  const [reason, setReason] = useState('');
  const [actionRequired, setActionRequired] = useState(
    'Stop feeding this product immediately. Hold or return it to Red Bridge Farm for a full refund.');
  const [subject, setSubject] = useState('');
  const [preview, setPreview] = useState<NoticePreview | null>(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ sent: number; failed: number } | null>(null);

  useEffect(() => {
    labelAPI.getFormulas()
      .then((res) => setFormulas(res.data.formulas || res.data || []))
      .catch(() => {});
  }, []);

  const criteria = useCallback(() => ({
    batch_numbers: batchNumbers.trim() || undefined,
    formula_id: formulaId || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  }), [batchNumbers, formulaId, dateFrom, dateTo]);

  const runTrace = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    setSendResult(null);
    try {
      const res = await recallAPI.trace(criteria());
      setResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Trace failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [criteria]);

  // Deep link: /mill/recall?batch=… prefills + auto-runs.
  useEffect(() => {
    if (searchParams.get('batch')) {
      runTrace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    try {
      const res = await recallAPI.traceCsv(criteria());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'recall_trace.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('CSV export failed');
    }
  };

  const previewNotice = async () => {
    setSending(true);
    setError('');
    try {
      const res = await recallAPI.notify({
        ...criteria(), reason, action_required: actionRequired,
        subject: subject || undefined,
      });
      setPreview(res.data);
      if (!subject && res.data.subject) setSubject(res.data.subject);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Preview failed');
    } finally {
      setSending(false);
    }
  };

  const confirmSend = async () => {
    if (!preview) return;
    if (!reason.trim()) {
      setError('A recall reason is required before sending.');
      return;
    }
    if (!window.confirm(
      `Send this recall notice to ${preview.recipient_count} recipient(s)? This cannot be undone.`)) {
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await recallAPI.notify({
        ...criteria(), reason, action_required: actionRequired,
        subject: subject || undefined, confirm: true,
      });
      setSendResult({ sent: res.data.sent, failed: res.data.failed });
      setPreview(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mill-page">
      <div style={{ marginBottom: '1rem' }}>
        <h1>Recall Lookup</h1>
        <p style={{ color: '#6b7280', margin: 0 }}>
          Trace production batches to every customer who may have received the feed
        </p>
      </div>

      {/* Search form */}
      <div className="mill-section" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 240px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600 }}>
              Batch number(s) — comma separated
            </label>
            <input
              type="text"
              value={batchNumbers}
              onChange={(e) => setBatchNumbers(e.target.value)}
              placeholder="BATCH-2026-014, BATCH-2026-015"
              style={{ width: '100%', padding: '0.5rem', minHeight: 44 }}
            />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600 }}>Formula</label>
            <select
              value={formulaId}
              onChange={(e) => setFormulaId(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', minHeight: 44 }}
            >
              <option value="">Any formula</option>
              {formulas.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 1 160px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600 }}>Milled from</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', minHeight: 44 }} />
          </div>
          <div style={{ flex: '0 1 160px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600 }}>Milled to</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', minHeight: 44 }} />
          </div>
          <button className="mill-btn primary" onClick={runTrace}
            disabled={loading || (!batchNumbers.trim() && !formulaId && !dateFrom && !dateTo)}
            style={{ minHeight: 44 }}>
            {loading ? 'Tracing…' : 'Run Trace'}
          </button>
        </div>
      </div>

      {error && <div className="mill-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {result && (
        <>
          {/* Summary */}
          <div className="mill-section" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>
              {result.batches.length} batch(es) · {result.summary.row_count} affected record(s) ·{' '}
              {result.summary.distinct_customers} distinct customer(s)
              {result.summary.total_lbs_known > 0 &&
                <> · {result.summary.total_lbs_known.toLocaleString()} lbs known</>}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {result.batches.map((b) => (
                <span key={b.batch_number} style={{
                  background: '#fee2e2', color: '#991b1b', padding: '0.25rem 0.6rem',
                  borderRadius: 6, fontWeight: 700 }}>
                  {b.batch_number}{b.milled_date ? ` (milled ${b.milled_date})` : ''}
                </span>
              ))}
              {Object.entries(result.summary.by_precision).map(([p, n2]) => (
                <span key={p} style={{
                  background: '#f3f4f6', color: PRECISION_COLOR[p] || '#374151',
                  padding: '0.25rem 0.6rem', borderRadius: 6 }}>
                  {PRECISION_LABEL[p] || p}: {n2}
                </span>
              ))}
            </div>
            {result.notes.map((note, i) => (
              <p key={i} style={{ color: '#92400e', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                ⚠ {note}
              </p>
            ))}
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
              <button className="mill-btn" onClick={exportCsv} disabled={!result.rows.length}
                style={{ minHeight: 44 }}>
                Export CSV
              </button>
              <button className="mill-btn primary" style={{ minHeight: 44 }}
                onClick={() => setShowNotice(true)} disabled={!result.rows.length}>
                Generate recall notice…
              </button>
            </div>
          </div>

          {/* Results table — sorted EXACT-first by the backend */}
          <div className="mill-section" style={{ overflowX: 'auto' }}>
            {result.rows.length === 0 ? (
              <p>No affected sales/orders found for these batches.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th>Precision</th><th>Channel</th><th>Customer</th><th>Contact</th>
                    <th>Product</th><th>Batch(es)</th><th>Qty</th><th>Date</th>
                    <th>Self-check</th><th>Ref / note</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <span style={{ color: PRECISION_COLOR[r.precision], fontWeight: 700 }}>
                          {PRECISION_LABEL[r.precision]}
                        </span>
                      </td>
                      <td>{r.channel}</td>
                      <td>{r.customer_name}{r.customer_kind === 'anonymous' && ' ⚠'}</td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {r.customer_email || <span style={{ color: '#b91c1c' }}>no email</span>}
                        {r.customer_phone && <><br />{r.customer_phone}</>}
                      </td>
                      <td>{r.product}</td>
                      <td style={{ fontWeight: 600 }}>{r.batch_numbers.join(', ')}</td>
                      <td>{r.quantity_lbs ? `${r.quantity_lbs.toLocaleString()} lbs` : r.quantity_display}</td>
                      <td>{r.date ? r.date.slice(0, 10) : '—'}</td>
                      <td>{r.self_check_supported ? '✓ tag' : '—'}</td>
                      <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.ref}{r.note && <><br />{r.note}</>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!result && !loading && (
        <div className="mill-section">
          <p>
            Enter one or more batch numbers (as printed on the bag tag), or find
            batches by formula and milled-date range, then run the trace.
          </p>
        </div>
      )}

      {/* Notice panel */}
      {showNotice && result && (
        <div className="mill-section" style={{ marginTop: '1rem', border: '2px solid #b91c1c' }}>
          <h3 style={{ marginTop: 0, color: '#b91c1c' }}>Recall notice — review before sending</h3>
          <p style={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Batches: {result.batches.map((b) =>
              `${b.batch_number}${b.milled_date ? ` (milled ${b.milled_date})` : ''}`).join(', ')}
          </p>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <label style={{ fontWeight: 600 }}>
              Subject
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="(auto-generated from batches if blank)"
                style={{ width: '100%', padding: '0.5rem' }} />
            </label>
            <label style={{ fontWeight: 600 }}>
              Reason / hazard (required to send)
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                placeholder="e.g. Elevated levels of X detected in routine testing…"
                style={{ width: '100%', padding: '0.5rem' }} />
            </label>
            <label style={{ fontWeight: 600 }}>
              Action required
              <textarea value={actionRequired} onChange={(e) => setActionRequired(e.target.value)}
                rows={2} style={{ width: '100%', padding: '0.5rem' }} />
            </label>
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="mill-btn" onClick={previewNotice} disabled={sending}
              style={{ minHeight: 44 }}>
              {sending ? 'Working…' : 'Preview (sends nothing)'}
            </button>
            {preview && (
              <button className="mill-btn primary" onClick={confirmSend}
                disabled={sending || !reason.trim()}
                style={{ minHeight: 44, background: '#b91c1c' }}>
                Confirm &amp; send to {preview.recipient_count} recipient(s)
              </button>
            )}
            <button className="mill-btn" onClick={() => { setShowNotice(false); setPreview(null); }}
              style={{ minHeight: 44 }}>
              Close
            </button>
          </div>

          {preview && (
            <div style={{ marginTop: '1rem' }}>
              <p>
                <strong>{preview.recipient_count}</strong> emailable recipient(s);{' '}
                <strong>{preview.excluded_count}</strong> cannot be emailed
                (physical/self-check outreach only).
              </p>
              {preview.excluded_count > 0 && (
                <details>
                  <summary>Excluded (no email)</summary>
                  <ul>
                    {preview.excluded.map((x, i) => (
                      <li key={i}>{x.customer_name} — {x.channel} ({x.ref}): {x.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              {preview.previews.map((p) => (
                <details key={String(p.self_check)} style={{ marginTop: '0.5rem' }}>
                  <summary>
                    Preview — {p.self_check ? 'tag self-check version' : 'no-tag version'}
                    {' '}(sample: {p.sample_recipient})
                  </summary>
                  <iframe title={`preview-${p.self_check}`} srcDoc={p.html}
                    style={{ width: '100%', height: 420, border: '1px solid #e5e7eb' }} />
                </details>
              ))}
            </div>
          )}
        </div>
      )}

      {sendResult && (
        <div className="mill-section" style={{ marginTop: '1rem', border: '2px solid #166534' }}>
          <h3 style={{ margin: 0, color: '#166534' }}>
            Recall notice sent — {sendResult.sent} delivered to Mailgun
            {sendResult.failed > 0 && `, ${sendResult.failed} FAILED (see EmailLog)`}
          </h3>
        </div>
      )}
    </div>
  );
};

export default RecallLookup;
